import { useEffect, useMemo, useRef, useState } from 'react'

import { ConnectError } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { PlanSubmissionKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  Menu,
  Select,
  type SelectOption,
  TabsComponent,
  TertiaryButton,
  TextButton,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaCloudArrowUpRegular,
  FaEllipsisRegular,
  FaGearRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import {
  DateStepper,
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { useLayoutPlanAutoSubmitNotices } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import {
  CreatePlanDialog,
  type CreatePlanDialogInput,
} from '~/components/planSubmit/CreatePlanDialog'
import { PlanAutoSubmitNoticeBanners } from '~/components/planSubmit/PlanAutoSubmitNoticeBanners'
import { iconSize } from '~/constants/iconSize'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { useCreatePlanDialogCandidates } from '~/hooks/planSubmit/useCreatePlanDialogCandidates'
import { useCreateProcurementPlan } from '~/hooks/planSubmit/useCreateProcurementPlan'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useLocationStateNotice } from '~/hooks/useLocationStateNotice'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  isWorkflowAutomationAvailable,
  useWorkflowAutomationAvailability,
} from '~/hooks/useWorkflowAutomationAvailability'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { convertAreaLabel } from '~/models/area'
import { startOfTodayJst } from '~/models/dateTime'
import { readCreatedPlanTargetDate } from '~/models/planSubmission/createdPlanNavigation'
import { detectNewlyFailedRows } from '~/models/planSubmission/transitionDetection'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { AutoResubmitSettingsModal } from '~/pages/ProcurementPlanList/components/AutoResubmitSettingsModal'
import { ProcurementPlanTable } from '~/pages/ProcurementPlanList/components/ProcurementPlanTable'
import { UnderDevPlaceholder } from '~/pages/ProcurementPlanList/components/UnderDevPlaceholder'
import { XmlUploadDialog } from '~/pages/ProcurementPlanList/components/XmlUploadDialog'
import {
  AREA_CODE_OPTIONS,
  DEFAULT_PERIOD_TAB,
  PERIOD_TAB_KEYS,
  type PeriodTabKey,
} from '~/pages/ProcurementPlanList/constants'
import {
  type ProcurementPlanRow,
  useListProcurementPlans,
} from '~/pages/ProcurementPlanList/hooks/useListProcurementPlans'
import { usePersistedProcurementPlanFilters } from '~/pages/ProcurementPlanList/hooks/usePersistedProcurementPlanFilters'
import {
  UploadFileTooLargeError,
  UploadPutFailedError,
  useUploadProcurementPlanXml,
} from '~/pages/ProcurementPlanList/hooks/useUploadProcurementPlanXml'
import { classifyPlanType } from '~/pages/ProcurementPlanList/models/classifyPlanType'
import { listTargetDateAfterCreate } from '~/pages/ProcurementPlanList/models/listTargetDateAfterCreate'
import { type TypeFilterValue } from '~/pages/ProcurementPlanList/models/persistedFilters'
import {
  PLAN_LIST_STATUS_FILTER_OPTIONS,
  countPlanListStatusFilters,
  filterByPlanListStatus,
} from '~/pages/ProcurementPlanList/models/planListStatusFilter'
import { toRowsWithBadge } from '~/pages/ProcurementPlanList/models/summary'

const ReasonTooltipIfAny = ({
  reason,
  children,
}: {
  reason: string | undefined
  children: React.ReactNode
}): React.ReactNode =>
  reason === undefined ? (
    children
  ) : (
    <ButtonTooltip content={reason}>{children}</ButtonTooltip>
  )

export const ProcurementPlanListPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { dateLockedReason, readonlyReason } = useWorkflowLockReasons()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  // workflow-back-bar-readonly AC-07/AC-11/AC-20: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、詳細リンクへの引き継ぎ・
  // read-only 制御 (完了済みケースで計画作成・アップロードを無効化) に使う
  // (fail-closed の理由は useCurrentWorkflowCase の JSDoc 参照)。
  const {
    caseId,
    isReadonly,
    isDateLocked,
    lockedTargetDate,
    isTargetDateReady,
    isTargetDateUnavailable,
  } = useCurrentWorkflowCase()

  // 計画期間タブ (procurement-plan-list-period-tab 設計 AC-001)。既定選択は
  // 「日次」。週間/月間/年間はタブ切替のみで中身は本 slice の対象外
  // (Decision #4: 未対応 placeholder を表示しフィルタ・アクションバー・テーブルは
  // 非表示)。URL / localStorage への永続化はしない (Decision #7)。
  // フィルタバー外の別軸 UI のため issue #6366 / AC-SC-031 の保持対象にも含めない。
  const [selectedPeriodTab, setSelectedPeriodTab] =
    useState<PeriodTabKey>(DEFAULT_PERIOD_TAB)
  const isDailyTab = selectedPeriodTab === 'daily'

  // フィルタバーの 5 フィルタ (対象日 / エリア / 種別 / 最新のみ / ステータス絞り込み) は
  // 画面遷移・再訪をまたいで localStorage に保持する (issue #6366 / AC-SC-031 / AC-PPL-032)。復元・直列化・
  // 破損値ガードは hook とその models/persistedFilters に閉じる。
  //
  // 対象日初期値は「明日」固定 (AC-04)。断面タブ撤去により一覧は特定の断面に
  // 連動せず、対象日ごとに 3 断面 (翌日(当日)/翌日/翌々日) を横断表示する
  // (AC-01)。翌日計画の対象日が最頻操作のため明日を初期値とする (spec
  // AC-014/AC-015 準拠)。
  //
  // エリアは複数選択 OR 条件 (procurement-plan-list-area-multi-select 設計
  // AC-01/AC-04、空配列 = 全エリア)。種別は初期値 'all'
  // (procurement-plan-list-type-filter 設計 AC-01)。
  const {
    targetDate: persistedTargetDate,
    setTargetDate,
    areaFilter,
    setAreaFilter,
    typeFilter,
    setTypeFilter,
    latestOnly,
    setLatestOnly,
    statusFilter,
    setStatusFilter,
  } = usePersistedProcurementPlanFilters()
  // AC-WFW-006a Decision 3: caseId 付きで開いている間はケース対象日を localStorage
  // の永続値より優先する (永続値へは書き戻さない = 直接アクセス時の値を汚さない)。
  // 取得完了までは永続値をそのまま表示するが、日付操作 UI は disabled のため
  // 実質操作不可 (Decision 1、fail-closed)。
  const targetDate = lockedTargetDate ?? persistedTargetDate
  // 進行中 mutation の onSuccess から現タブを参照するための ref (state ではなく
  // ref なのは、onSuccess の closure が古い state を掴むと race で dialog が
  // 復活するため)。同期は下の useEffect で行う。
  const isDailyTabRef = useRef(isDailyTab)
  useEffect(() => {
    isDailyTabRef.current = isDailyTab
  }, [isDailyTab])
  const periodTabItems: Array<{ key: PeriodTabKey; label: string }> = useMemo(
    () =>
      PERIOD_TAB_KEYS.map((key) => ({
        key,
        label: t(`page:procurementPlanList.periodTab.${key}`),
      })),
    [t],
  )

  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false)
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [isAutoResubmitSettingsOpen, setAutoResubmitSettingsOpen] =
    useState(false)
  // 自動再提出の設定は feature flag (全組織で一斉に公開) で隠す。 取得中・取得失敗も false と同じく
  // 入口を出さない (使えるかが確定するまで出さない)
  const autoResubmitAvailability = useWorkflowAutomationAvailability()
  const isAutoResubmitAvailable = isWorkflowAutomationAvailable(
    autoResubmitAvailability,
  )

  const selectedAreaCodes: AreaCode[] = AREA_CODE_OPTIONS.filter((opt) =>
    areaFilter.includes(opt.value),
  ).map((opt) => opt.areaCode)

  const areaSelectOptions: SelectOption[] = useMemo(
    () =>
      AREA_CODE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: convertAreaLabel(opt.areaCode),
      })),
    [t],
  )

  const selectedAreaOptions: SelectOption[] = areaSelectOptions.filter((opt) =>
    areaFilter.includes(opt.value),
  )

  // 種別フィルタ判定用の BG マスタ全件 (bgCode → licenseType)。既存
  // useListBgMasters hook を再利用する (procurement-plan-list-type-filter
  // 設計 §4: proto / BFF 変更なしの方針、frontend 単独で判定する)。
  const { data: bgMasterListData, isSuccess: isBgMasterReady } =
    useListBgMasters()
  const bgMasterMap: Map<string, LicenseType> = useMemo(
    () =>
      new Map(
        (bgMasterListData?.items ?? []).map((item) => [
          item.bgCode,
          item.licenseType,
        ]),
      ),
    [bgMasterListData],
  )
  const typeFilterOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: t('page:procurementPlanList.filter.typeAll') },
      { value: 'bg', label: t('page:procurementPlanList.filter.typeBg') },
      {
        value: 'retail',
        label: t('page:procurementPlanList.filter.typeRetail'),
      },
    ],
    [t],
  )
  const selectedTypeFilterOption =
    typeFilterOptions.find((opt) => opt.value === typeFilter) ?? null

  // timeframe を指定せずに呼ぶ (= optional field 不在) ことで、対象日の
  // 全断面 (翌日(当日)/翌日/翌々日) を横断取得する (AC-001/AC-003)。
  // enabled: 計画期間タブが「日次」以外のときは一覧を表示しないため取得しない
  // (procurement-plan-list-period-tab 設計 Decision #4)。caseId 付きで開いている
  // 間はケース対象日が確定するまで取得しない (AC-WFW-006a Decision 1)。
  const { data, isLoading, isError } = useListProcurementPlans({
    targetPeriodStartDate: targetDate,
    areaCodes: selectedAreaCodes,
    includeAllVersions: !latestOnly,
    enabled: isDailyTab && isTargetDateReady,
  })

  // 失敗状態への遷移検知トースト (plansubmit-error-detail design AC-PLC-033-2、
  // 決定 #4: 一覧表示中のみ・通知のみで遷移や操作を伴わない)。polling で得た
  // 直近の行と前回の行を比較し、警告なし→警告あり (作成失敗/差戻し/提出失敗)
  // へ遷移した行があれば toast で知らせる。 targetDate / フィルタ変更で
  // query key が変わったときの誤検知 (別データセットとの比較) を避けるため、
  // previousRowsRef は data 変更のたびに常に更新する。
  //
  // 非日次タブ滞在中は一覧が非表示のため、切替前に発射済みの応答が解決した時点で
  // 失敗遷移トーストが「一覧を表示していない画面」に出るのを防ぐため isDailyTab で
  // gate する (period-tab 設計 Decision #4「非日次時は開発中 placeholder のみ」)。
  const previousRowsRef = useRef<ProcurementPlanRow[] | undefined>(undefined)
  useEffect(() => {
    if (!isDailyTab) return
    if (data === undefined) return
    const newlyFailed = detectNewlyFailedRows(
      previousRowsRef.current,
      data.rows,
    )
    if (newlyFailed.length > 0) {
      showErrorToast({
        title: t('page:procurementPlanList.transitionToast.title'),
        description: t('page:procurementPlanList.transitionToast.description', {
          count: newlyFailed.length,
        }),
      })
    }
    previousRowsRef.current = data.rows
  }, [data, showErrorToast, t, isDailyTab])

  // 計画作成ダイアログ (AC-09) の入力候補: BG マスタ全件 + 組織の業務エリア設定。
  // ダイアログは一覧のフィルタ state (areaFilter 等) と完全に分離した独立フォーム
  // であり、これらは候補データの供給のみに使う (design §2: 「今日」ボタンの
  // 断面不整合の構造的解消)。対象日だけは例外的に一覧の現在値を初期値として
  // ダイアログへ渡す (`initialTargetDate`、AC-PLC-040) が、渡すのは値のみで
  // setter は渡さないため、開いた瞬間のコピー止まりで分離は保たれる
  // (`tmp/designing-feature/create-dialog-target-date-initial-value.md`
  // Decision #1/#4)。
  //
  // ダイアログ → 一覧の書き戻しは **作成完了時だけ** 存在する (AC-PLC-054。
  // `handleCreate` の onSuccess が作成した計画の対象日を `setTargetDate` する)。
  // ダイアログを開いている間の対象日変更は一覧側に反映しない (AC-PLC-040 ※)。
  const createDialogCandidates = useCreatePlanDialogCandidates()

  // 詳細の「計画を作り直す」 (AC-PLC-020-2) で作成して来た時も、作成した計画の
  // 対象日へ対象日フィルタを合わせる (AC-PLC-054)。
  const [createdPlanTargetDate, dismissCreatedPlanTargetDate] =
    useLocationStateNotice(readCreatedPlanTargetDate)
  useEffect(() => {
    if (createdPlanTargetDate === undefined) return
    const nextTargetDate = listTargetDateAfterCreate(
      createdPlanTargetDate,
      isDateLocked,
    )
    if (nextTargetDate !== undefined) {
      setTargetDate(nextTargetDate)
    }
    dismissCreatedPlanTargetDate()
  }, [
    createdPlanTargetDate,
    isDateLocked,
    setTargetDate,
    dismissCreatedPlanTargetDate,
  ])

  const handleAreaChange = (options: readonly SelectOption[]): void => {
    setAreaFilter(options.map((opt) => opt.value))
  }

  // 「全エリア」ボタン (AC-016): エリアフィルタを解除し全エリア表示に戻す。
  const handleAreaReset = (): void => {
    setAreaFilter([])
  }

  const { mutate: createPlan, isPending: isCreating } =
    useCreateProcurementPlan()

  const handleCreate = (input: CreatePlanDialogInput): void => {
    createPlan(input, {
      onSuccess: () => {
        // AC-PLC-054 (切り替える条件は `listTargetDateAfterCreate`)。
        //
        // 下の非日次 gate より **前** に置く。gate が防ぐのは placeholder-only
        // 画面へ描画 (toast / ダイアログ) が浮上することで、描画を伴わない
        // 対象日 state の更新は同 invariant を破らない。gate の後ろに置くと、
        // mutation in-flight 中にタブを切り替えただけで AC-PLC-054 の追随が
        // 丸ごと落ちる (PR #8521 レビュー指摘 ikdene 4089293474)。
        const nextTargetDate = listTargetDateAfterCreate(
          input.targetPeriodStartDate,
          isDateLocked,
        )
        if (nextTargetDate !== undefined) {
          setTargetDate(nextTargetDate)
        }
        // 非日次滞在中は placeholder のみを描画する (period-tab 設計 Decision #4)。
        // in-flight mutation の遅延到着 callback が global toast を発火させると
        // placeholder-only 画面に浮上して invariant を破るため、ref で gate する。
        if (!isDailyTabRef.current) return
        setCreateDialogOpen(false)
        showSuccessToast({
          title: t('page:procurementPlanList.createButton.successTitle'),
          description: t(
            'page:procurementPlanList.createButton.successDescription',
          ),
        })
      },
      onError: () => {
        if (!isDailyTabRef.current) return
        showErrorToast({
          title: t('page:procurementPlanList.createButton.errorTitle'),
        })
      },
    })
  }

  // 取込は同期完結 (docs/adr/plan-submit-xml-import-synchronous.md) なので、
  // onSuccess の時点で計画は「未提出」で登録済み (AC-PLC-007)。
  const { mutate: uploadXml, isPending: isUploading } =
    useUploadProcurementPlanXml()

  // 手順は hook (useUploadProcurementPlanXml) が持つ — 発行 RPC → 上限超過なら中止
  // (AC-030/031、上限は発行 response の max_content_bytes) → 発行 URL へ PUT →
  // 取込 RPC。形式・フォーマット検証は backend が行い、その文言は rawMessage をそのまま出す。
  const handleFileSelected = (file: File): void => {
    setUploadDialogOpen(false)
    uploadXml(file, {
      onSuccess: () => {
        // 非日次滞在中は placeholder のみ (period-tab 設計 Decision #4)。
        // in-flight upload の遅延到着 onSuccess が toast を発火させると
        // placeholder-only 画面に浮上する race を防ぐ。
        if (!isDailyTabRef.current) return
        showSuccessToast({
          // 取込 RPC は 1 ファイルから 1 計画を作る (UploadProcurementPlanXmlResponse.id)。
          title: t('page:procurementPlanList.upload.successTitle', {
            count: 1,
          }),
        })
      },
      onError: (err) => {
        // 非日次滞在中は placeholder のみ (period-tab 設計 Decision #4)。
        // in-flight upload の遅延到着 onError が toast を発火させると
        // placeholder-only 画面に浮上する race を防ぐ。
        if (!isDailyTabRef.current) return
        const errorTitle = t('page:procurementPlanList.upload.errorTitle')
        if (err instanceof UploadFileTooLargeError) {
          showErrorToast({
            title: errorTitle,
            description: t('page:procurementPlanList.upload.fileTooLarge', {
              maxSizeMB: Number(err.maxContentBytes) / (1024 * 1024),
            }),
          })
          return
        }
        if (err instanceof UploadPutFailedError) {
          showErrorToast({
            title: errorTitle,
            description: t('page:procurementPlanList.upload.putFailed'),
          })
          return
        }
        // err.rawMessage (not err.message, which prefixes the Connect
        // code as "[invalid_argument] ...") is the backend's exact §5
        // エラー表 wording — frontend passes it through unchanged.
        showErrorToast({
          title: errorTitle,
          description: err instanceof ConnectError ? err.rawMessage : undefined,
        })
      },
    })
  }

  const allRows = data?.rows ?? []
  const allRowsWithBadge = toRowsWithBadge(allRows)

  // 種別フィルタ (procurement-plan-list-type-filter 設計 §5): client-side で
  // classifyPlanType の写像に基づき絞り込む (BFF 全件返却済みの行に適用、既存
  // エリア / 日付 / 最新のみフィルタと AND 結合、AC-07)。'all' は無条件通過。
  // BG マスタ不在行 (classifyPlanType が undefined) は 'all' でのみ表示される
  // (AC-05)。BG マスタ未取得 (loading / error) の間はフィルタを適用せず全行を
  // 通過させる (= 一覧表示を壊さない)。取得成功後は空 Map でも通常どおり
  // classifyPlanType を適用する。
  // 表示順は backend の返却順のまま並べ替えない (AC-PPL-018 / §ソート順: 最終更新日時の
  // 降順。internalapi ListPlanSubmissionsResponse.submissions の contract)。filter は順序を保つ。
  const typeFilteredRowsWithBadge = useMemo(() => {
    if (typeFilter === 'all' || !isBgMasterReady) return allRowsWithBadge
    return allRowsWithBadge.filter(
      (item) => classifyPlanType(item.row.bgCode, bgMasterMap) === typeFilter,
    )
  }, [allRowsWithBadge, typeFilter, bgMasterMap, isBgMasterReady])

  // ステータス絞り込み (AC-PPL-021)。件数は種別まで絞った行から数える (design の件数と同じ母集合)。
  const statusFilterCounts = useMemo(
    () => countPlanListStatusFilters(typeFilteredRowsWithBadge),
    [typeFilteredRowsWithBadge],
  )
  const visibleRowsWithBadge = useMemo(
    () => filterByPlanListStatus(typeFilteredRowsWithBadge, statusFilter),
    [typeFilteredRowsWithBadge, statusFilter],
  )

  // 計画期間タブ切替 handler。非日次への切替時にダイアログの open state も閉じる
  // (period-tab 設計 Decision #4「非日次時は開発中 placeholder のみ」)。ダイアログは日次分岐内で
  // unmount されるが open state は残るため、閉じないと日次に戻った瞬間に前のダイアログが浮上する。
  //
  // `isDailyTabRef` は setSelectedPeriodTab より **先** に同期更新する。
  // useEffect は commit 後に走るため、setState → 再 render commit の前に mutation の
  // onSuccess が microtask で解決すると ref が前タブの値のまま参照される race がある
  // (下段 useEffect の同期はこの handler を経由しない state 変化への safety net)。
  const handlePeriodTabChange = (key: PeriodTabKey): void => {
    isDailyTabRef.current = key === 'daily'
    if (key !== 'daily') {
      setCreateDialogOpen(false)
      setUploadDialogOpen(false)
      setAutoResubmitSettingsOpen(false)
    }
    setSelectedPeriodTab(key)
  }

  // 「今日」ボタンは spec AC-015 準拠で targetDate を本日に戻す。断面タブ撤去
  // (procurement-plan-list-three-section 設計) により一覧は timeframe を
  // 指定せず全断面を横断取得するため、旧実装にあった「timeframe × targetDate
  // の semantic mismatch」(= 特定断面タブ選択中に本日へ戻すと空表示になる懸念)
  // は構造的に解消されている。計画作成ダイアログは一覧の targetDate と独立した
  // 状態を持つため (design §2)、本ボタンの影響を受けない。
  //
  // 基準日は JST 壁時計 (`startOfTodayJst`)。PR #6542 レビュー指摘: 旧実装は
  // `serverNow()` を端末ローカル壁時計で丸めており、`targetDate`（一覧初期値 = JST の本日 /
  // 作成ダイアログの提出区分判定）が既に JST 軸なのに本ボタンだけローカル軸の
  // ままだと、非 JST 端末で JST 深夜前後に「今日」が 1 日ズレて表示と業務判定が
  // 食い違う。
  const handleDateToday = (): void => {
    setTargetDate(startOfTodayJst())
  }

  const planAutoSubmitNotices = useLayoutPlanAutoSubmitNotices()

  const breadcrumbItems = [
    { label: t('common:nav.plan') },
    { label: t('common:route.planProcurement'), bold: true },
  ]

  return (
    <WorkflowPageLayout
      breadcrumbItems={breadcrumbItems}
      // 自動で提出・出し直しできなかった計画の警告バナー (AC-PPL-035)。対象日・フィルタ・計画期間タブ
      // によらず全件を出すので、ページ最上部の通知に置く。
      notices={
        <PlanAutoSubmitNoticeBanners
          notices={planAutoSubmitNotices}
          planKind={PlanSubmissionKind.DEMAND_PROCUREMENT}
        />
      }
    >
      <PageBody>
        {/* 画面冒頭の説明文 (design `.page-description`)。 位置は 8080 と同じ
            `PageBody` 直下の先頭 = 計画期間タブより上
            (`plan/procurement.html:99` / `plan/gen-sales-plan.html:115`)。
            PR #6544 が 8080 の `.page-description` を全面突合した際、 8080 側に
            しか無い 63 件を「大半は app 未実装画面」としてまとめて対象外にしたが、
            本 2 画面は実装済みで取りこぼしていた
            (`design/ui-feasibility/screens/gen-sales-plan.md:40` が
            「dev で欠落 (乖離#1)」と記録済み)。 */}
        <PageIntro description={t('page:procurementPlanList.description')} />
        {/* 計画期間タブ (procurement-plan-list-period-tab 設計 AC-001)。
            PageBody 直下の先頭 (WorkflowBackBar の下、FilterToolbar の上、
            Decision #5)。日次以外はタブ切替のみで中身は本 slice の対象外。 */}
        <TabsComponent
          items={periodTabItems}
          onSelect={handlePeriodTabChange}
          selected={selectedPeriodTab}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />

        {isDailyTab ? (
          <>
            {/* フィルターバー (対象日ナビゲーション / エリア / 最新のみ) — design: 計画期間タブの
                下。position:relative + zIndex:10 は Select ドロップダウンの重なり順維持のため既存値を移設 */}
            <div style={{ position: 'relative', zIndex: 10 }}>
              <FilterToolbar>
                {/* 対象日ナビゲーション — 前日/翌日ボタン + 日付 input + 今日ボタン。
                    AC-WFW-006a: caseId 付きで開いている間は disabled + 理由 tooltip +
                    Badge「ケース対象日に固定」を出す (Decision 6)。 */}
                <FilterSection
                  label={t('page:procurementPlanList.filter.targetDate')}
                >
                  <DateStepper
                    dateAriaLabel={t(
                      'page:procurementPlanList.filter.targetDate',
                    )}
                    disabled={isDateLocked}
                    disabledReason={dateLockedReason}
                    onChange={setTargetDate}
                    onTodayClick={handleDateToday}
                    todayLabel={t('page:procurementPlanList.filter.dateToday')}
                    value={targetDate}
                  />
                  {lockedTargetDate !== undefined && (
                    <Badge status="info">
                      {t('common:filter.caseLockedBadge')}
                    </Badge>
                  )}
                </FilterSection>

                {/* 種別 (procurement-plan-list-type-filter 設計 AC-01/AC-06)。
                    design 準拠で対象日とエリアの間に配置する単一選択 Select。 */}
                <FilterSection
                  label={t('page:procurementPlanList.filter.type')}
                >
                  {/* 幅は外側の Box。 DS Select に chakraStyles を渡すと DS 側の
                      chakraStyles が丸ごと置き換わり、 選択時背景・list の font-size・
                      ▽ 左の区切り線が失われる。 */}
                  <Box minWidth="150px" css={selectFontFix}>
                    <Select
                      aria-label={t('page:procurementPlanList.filter.type')}
                      isMulti={false}
                      onChange={(opt) => {
                        if (opt !== null) {
                          setTypeFilter(opt.value as TypeFilterValue)
                        }
                      }}
                      options={typeFilterOptions}
                      selectedOptions={selectedTypeFilterOption}
                      size="md"
                    />
                  </Box>
                </FilterSection>

                {/* エリア (複数選択 OR 条件、 procurement-plan-list-area-multi-select
                    設計 AC-01) + 「全エリア」ボタン (AC-016: フィルタ解除) */}
                <FilterSection
                  label={t('page:procurementPlanList.filter.area')}
                >
                  <Box minWidth="200px" css={selectFontFix}>
                    <Select
                      aria-label={t('page:procurementPlanList.filter.area')}
                      isMulti
                      onChange={(opts) => {
                        handleAreaChange(opts)
                      }}
                      options={areaSelectOptions}
                      /* [2026-09-29 デザイナー指摘] 下のフィルタ解除**ボタン**は
                         placeholder と別キーのまま「全エリア」 — ボタンは操作なので
                         「すべて」 だと「全選択」 と読めてしまう。 */
                      placeholder={t(
                        'page:procurementPlanList.filter.areaAllPlaceholder',
                      )}
                      selectedOptions={selectedAreaOptions}
                      size="md"
                    />
                  </Box>
                  <TextButton onClick={handleAreaReset}>
                    {t('page:procurementPlanList.filter.areaAll')}
                  </TextButton>
                </FilterSection>

                {/* 最新のみ — グループラベルを持たない単独 checkbox のため FilterSection
                    でなく直接子にする */}
                <Checkbox
                  checked={latestOnly}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => setLatestOnly(details.checked === true)}
                >
                  {t('page:procurementPlanList.filter.latestOnly')}
                </Checkbox>

                {/* ステータス絞り込み (AC-PPL-021)。選択肢と順は spec (plan-common.md §1)、
                    位置と形は design の #f-status-tabs。件数は取得前は出さない。 */}
                <FilterSection
                  label={t('page:procurementPlanList.statusFilter.label')}
                >
                  <AccessibleSegmentedControl
                    aria-label={t(
                      'page:procurementPlanList.statusFilter.label',
                    )}
                    name="procurement-plan-list-status-filter"
                    onSelect={setStatusFilter}
                    options={PLAN_LIST_STATUS_FILTER_OPTIONS.map((option) => ({
                      value: option,
                      // 件数は design の「ラベル (N)」の形で併記する
                      label:
                        data === undefined
                          ? t(`page:procurementPlanList.statusFilter.${option}`)
                          : `${t(`page:procurementPlanList.statusFilter.${option}`)} (${statusFilterCounts[option]})`,
                    }))}
                    selected={statusFilter}
                  />
                </FilterSection>
              </FilterToolbar>
            </div>

            {/* アクションバー — design: 「計画作成」ボタンを DS Menu 化 (項目:
                計画作成 / アップロード、`AdminOcctoMaster` の
                MasterTableActionBar と同形の Menu パターン踏襲)。
                手組みフィルタ行 (棚卸し対象) ではないため raw のまま維持 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}
            >
              {/* 計画作成は **split button** (主 = 個別作成を 1 クリックで即実行 /
                  ▾ = 代替の取込手段)。8080 `plan/procurement.html:183-194` と同形で、
                  `design/ui-feasibility/components/split-button.md` が

                    dropdown (区切り線なし) … **ただし作成 / 追加導線には使わない**
                    (作成 / 追加は既定の 1 手を決めて split にする)

                  と明記している (2026-09-16 デザイナー指摘)。旧実装はボタン全体が
                  menu を開く dropdown で、しかも **menu の先頭に主ボタンと同じ
                  「計画作成」を再掲**していた (= `MasterTableActionBar` の
                  「主ボタンを ▾ に再掲しない」と逆)。

                  variant は **`TertiaryButton`** (2026-09-17 デザイナー指示
                  「8080 同様、primary ではなく tertiary ベース色に (白背景の
                  border あり)」)。

                  当初は「操作帯の子孫に他の `btn-primary` があるか」で決める
                  規則を当て、この帯に他の primary が無いので primary としていた
                  (8080 が tertiary なのは 8080 の帯に提出ボタンが同居するため、
                  という読み)。実装側にその同居が無いのは事実だが、**見え方を
                  8080 と揃える**判断が優先する。 */}
              <Flex>
                {/* AC-WFW-014: 読み取り専用の理由は主ボタン・caret の両方に付ける */}
                <ButtonTooltip content={readonlyReason}>
                  <TertiaryButton
                    borderTopRightRadius={0}
                    borderBottomRightRadius={0}
                    /* [PR #8203 レビュー指摘 / ikdene] **取込中は主ボタンも塞ぐ**。
                       split 化する前は Menu trigger 1 つの button が
                       `disabled={isReadonly} loading={isUploading}` を持ち、 XML 取込中は
                       作成・取込の両方が塞がっていた。 split 後 `isUploading` が caret 側
                       だけに残り、 取込 mutation が in-flight の間も作成ダイアログを開いて
                       `createPlan` を走らせられる状態になっていた (後退)。
                       spinner は取込を起動した caret 側にだけ出し (2 つ出ると何の処理中か
                       曖昧になる)、 主ボタンは disabled で塞ぐ。 design SoT
                       `design/ui-feasibility/components/split-button.md:315-317` の
                       「主が押せないのに ▾ だけ開ける状態を作らない」 と同じ考え方を
                       busy にも当てる。 */
                    disabled={isReadonly || isUploading}
                    onClick={() => {
                      setCreateDialogOpen(true)
                    }}
                    /* [2026-09-17 / Des 指摘] sm (32px) → md (40px)。 design の split button
                       (`plan/procurement.html:184-187`) は共通 `.btn` の既定高さ 40px で、
                       caret 側も `.ecl-split-btn-caret { height: 40px }` を明示している。
                       sm のままだと操作帯の他のボタン・入力欄 (40px) と高さが揃わない。 */
                    size="md"
                  >
                    <HStack gap={1}>
                      <FaPlusRegular />
                      <span>
                        {t('page:procurementPlanList.createButton.label')}
                      </span>
                    </HStack>
                  </TertiaryButton>
                </ButtonTooltip>
                {/* ▾ は icon-only。**`aria-label` + `title` で名前を出す。**

                    [rule 逸脱] icon-only button に DS Tooltip を持たせる規約を
                    **split button の caret では採らない** (PR #8203 レビュー指摘への回答):

                    1. repo の split button 前例 `MasterTableActionBar` (OCCTO マスタ
                       7 タブ等で使用) の caret は **`aria-label` のみ**。ここだけ
                       Tooltip を足すと split の作りが画面ごとに割れる
                    2. `ButtonTooltip` で包むと **`Menu` の直下の子が trigger でなくなり
                       menu が開かない**。`Menu` の外側を包むと開くようになるが、今度は
                       **同一画面の別 Tooltip (エラー詳細) の本文が二重に描画され**、
                       関係のないテスト 2 件が落ちる (再現確認済み)

                    `title` は Tooltip 非対応環境向けの保険として前例に足してある
                    (`ButtonTooltip` の JSDoc の位置づけ)。caret に DS Tooltip を
                    載せる是非は `MasterTableActionBar` ごと揃える話なので別途。 */}
                {/* Menu の直下の子は trigger でなければならないので、理由がある時 (= caret は
                    disabled で menu を開けない) だけ包む */}
                <ReasonTooltipIfAny reason={readonlyReason}>
                  <Menu
                    content={[
                      {
                        icon: <FaCloudArrowUpRegular />,
                        label: t('page:procurementPlanList.menu.itemUpload'),
                        value: 'upload',
                        onClick: () => {
                          setUploadDialogOpen(true)
                        },
                      },
                    ]}
                  >
                    {/* 継ぎ目は design SoT
                        (`design/ui-feasibility/components/split-button.md:171-175`) に揃える:

                        - 線色は **`color-mix(in srgb, currentColor 25%, transparent)`**。
                          文字色から作るので **variant ごとに色を書かない** (SoT 明記)。
                          当初は `Border.lowEmphasis` を固定で当てていたが、 variant を
                          変えると線が追随しない
                        - **`margin-left: -1px`** で主ボタンと caret の border を重ねて
                          1 本にする。 無いと 1px の border が 2 本並んで**継ぎ目が
                          二重線**に見える (PR #8203 レビュー指摘 / ikdene) */}
                    <TertiaryButton
                      marginLeft="-1px"
                      borderLeftWidth="1px"
                      borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                      borderTopLeftRadius={0}
                      borderBottomLeftRadius={0}
                      aria-label={t(
                        'common:masterTableActionBar.createMenuAriaLabel',
                      )}
                      disabled={isReadonly}
                      loading={isUploading}
                      paddingX={2}
                      /* 主ボタンと同じ高さにする (split button は 2 つで 1 つの部品)。 */
                      size="md"
                      title={t(
                        'common:masterTableActionBar.createMenuAriaLabel',
                      )}
                    >
                      <FaChevronDownRegular />
                    </TertiaryButton>
                  </Menu>
                </ReasonTooltipIfAny>
              </Flex>
              {/* AC-PAR-010: 「⋯」→「自動再提出の設定」。 設定は対象日・ワークフローに
                  依らず、 一覧を使える人は誰でも閲覧・変更できるので、 読取専用・取込中でも
                  押せる (design `plan/procurement.html` の `.sl-more-menu`)。 feature
                  flag が ON と確定した時だけ出す (取得中・取得失敗・OFF は出さない)。

                  [rule 逸脱] icon-only の trigger に DS Tooltip を持たせず `aria-label` +
                  `title` にする。 上の caret と同じく `Menu` の直下の子は trigger で
                  なければならず、 Tooltip で包むと menu が開かないため。 */}
              {isAutoResubmitAvailable && (
                <Menu
                  content={[
                    {
                      icon: <FaGearRegular />,
                      label: t(
                        'page:procurementPlanList.menu.itemAutoResubmitSettings',
                      ),
                      value: 'auto-resubmit-settings',
                      onClick: () => {
                        setAutoResubmitSettingsOpen(true)
                      },
                    },
                  ]}
                >
                  <TertiaryButton
                    width="40px"
                    minWidth="40px"
                    aria-label={t('page:procurementPlanList.menu.moreActions')}
                    paddingX={0}
                    size="md"
                    title={t('page:procurementPlanList.menu.moreActions')}
                  >
                    <FaEllipsisRegular boxSize={iconSize.labeledButton} />
                  </TertiaryButton>
                </Menu>
              )}
            </div>

            {/* テーブル — AC-01: fetch 失敗時はテーブル位置に Alert バナー 1 本のみを
                表示し (フィルタ・アクションバーは操作可のまま)、空状態とは区別する。
                再読み込み導線は付けない (design §4「Behavior (一覧)」、AC-PLC-009 は
                導線を要求していない)。
                条件は `data === undefined && isError` で初回取得失敗のみに限定する
                (成功後の背景 refetch 失敗で表示済みの table を Alert に置換しない)。
                ケース対象日を得られない時も一覧を取得できないので、同じ query key に
                残ったキャッシュの有無によらず同じ Alert を出す (取得を止めたままの空
                table や別日の一覧を見せない)。 */}
            {isTargetDateUnavailable || (data === undefined && isError) ? (
              <CompactAlert
                status="error"
                title={t('page:procurementPlanList.error.fetchFailed')}
              />
            ) : (
              <PageScrollArea>
                <ProcurementPlanTable
                  caseId={caseId}
                  isLoading={isLoading}
                  rows={visibleRowsWithBadge}
                />
              </PageScrollArea>
            )}

            {/* ダイアログは日次ブランチ内に配置する。日次でしか発動しない操作
                (計画作成・アップロード) の UI が非日次画面 (placeholder のみ) に
                浮上するのを防ぐため (procurement-plan-list-period-tab 設計 Decision #4
                「非日次時は開発中 placeholder のみ」)。unmount だけでは open state が
                残るため、handlePeriodTabChange で state 側も close する。global overlay の toast (`useSuccessToast` /
                `useErrorToast`) は unmount で消せないため、`isDailyTabRef` を
                createPlan / uploadXml の onSuccess / onError で共通に gate して
                同 invariant を保証する。 */}
            <CreatePlanDialog
              bgMasterItems={createDialogCandidates.bgMasterItems}
              bgMasterPeriods={createDialogCandidates.bgMasterPeriods}
              bgNames={createDialogCandidates.bgNames}
              businessAreas={createDialogCandidates.businessAreas}
              initialTargetDate={targetDate}
              isCandidateDataReady={createDialogCandidates.isCandidateDataReady}
              isCreating={isCreating}
              isOpen={isCreateDialogOpen}
              onClose={() => {
                setCreateDialogOpen(false)
              }}
              onCreate={handleCreate}
            />

            <XmlUploadDialog
              isOpen={isUploadDialogOpen}
              onClose={() => {
                setUploadDialogOpen(false)
              }}
              onFileSelected={handleFileSelected}
            />

            {isAutoResubmitSettingsOpen && (
              <AutoResubmitSettingsModal
                onClose={() => {
                  setAutoResubmitSettingsOpen(false)
                }}
              />
            )}
          </>
        ) : (
          // 週間/月間/年間タブ選択時の代替表示 (procurement-plan-list-period-tab
          // 設計 Decision #4)。spec スコープ外のため design HTML に無い。
          // 未実装ルート用の PlaceholderPage (`common:placeholder.title`) と
          // 見た目・文言を揃えたタブ scoped 版を local component として置く。
          <UnderDevPlaceholder
            tabLabel={t(
              `page:procurementPlanList.periodTab.${selectedPeriodTab}`,
            )}
          />
        )}
      </PageBody>
    </WorkflowPageLayout>
  )
}
